import {
  calculateSpineWidthMm,
  type PhotobookProject,
  type PageElement,
  type ProductProfile,
} from '@fotoboek/domain';
import type { SelectionTarget, ViewState, CoverPanelKey } from '../../../state/projectStore';

/** Fixed canvas scale: pixels per millimetre at 100% zoom. */
export const PX_PER_MM = 3.2;

export type SceneItem = {
  target: SelectionTarget;
  originMm: { x: number; y: number };
  sizeMm: { width: number; height: number };
  bleedMm: number;
  safeMarginMm: number;
  backgroundColor: string;
  elements: PageElement[];
};

export type Scene = {
  items: SceneItem[];
  totalSizeMm: { width: number; height: number };
  gutterLinesMm: number[];
};

export function buildScene(
  project: PhotobookProject,
  view: ViewState,
  profile: ProductProfile,
): Scene {
  const pageSize = profile.pageSizeMm;

  if (view.mode === 'cover') {
    const spineW = calculateSpineWidthMm(
      project.pages.length,
      project.settings.paperStockId,
      profile,
    );
    const panel = (key: CoverPanelKey) => project.cover.panels[key];
    const items: SceneItem[] = [
      {
        target: { scope: 'cover', panel: 'back' },
        originMm: { x: 0, y: 0 },
        sizeMm: pageSize,
        bleedMm: profile.cover.wrapBleedMm,
        safeMarginMm: profile.safeMarginMm,
        backgroundColor: panel('back').background.color,
        elements: panel('back').elements,
      },
      {
        target: { scope: 'cover', panel: 'spine' },
        originMm: { x: pageSize.width, y: 0 },
        sizeMm: { width: spineW, height: pageSize.height },
        bleedMm: profile.cover.wrapBleedMm,
        safeMarginMm: Math.min(profile.safeMarginMm, spineW / 4),
        backgroundColor: panel('spine').background.color,
        elements: panel('spine').elements,
      },
      {
        target: { scope: 'cover', panel: 'front' },
        originMm: { x: pageSize.width + spineW, y: 0 },
        sizeMm: pageSize,
        bleedMm: profile.cover.wrapBleedMm,
        safeMarginMm: profile.safeMarginMm,
        backgroundColor: panel('front').background.color,
        elements: panel('front').elements,
      },
    ];
    return {
      items,
      totalSizeMm: { width: pageSize.width * 2 + spineW, height: pageSize.height },
      gutterLinesMm: [],
    };
  }

  const spreadIndex = view.spreadIndex;
  const left = project.pages.find((p) => p.order === spreadIndex * 2);
  const right = project.pages.find((p) => p.order === spreadIndex * 2 + 1);
  const items: SceneItem[] = [];
  if (left) {
    items.push({
      target: { scope: 'page', pageId: left.id },
      originMm: { x: 0, y: 0 },
      sizeMm: pageSize,
      bleedMm: profile.bleedMm,
      safeMarginMm: profile.safeMarginMm,
      backgroundColor: left.background.color,
      elements: left.elements,
    });
  }
  if (right) {
    items.push({
      target: { scope: 'page', pageId: right.id },
      originMm: { x: pageSize.width, y: 0 },
      sizeMm: pageSize,
      bleedMm: profile.bleedMm,
      safeMarginMm: profile.safeMarginMm,
      backgroundColor: right.background.color,
      elements: right.elements,
    });
  }
  return {
    items,
    totalSizeMm: { width: pageSize.width * 2, height: pageSize.height },
    gutterLinesMm: items.length === 2 ? [pageSize.width] : [],
  };
}

export function spreadCount(project: PhotobookProject): number {
  return Math.ceil(project.pages.length / 2);
}
